/**
 * MuiProvidersList - Optimized Suppliers/Providers List Page
 *
 * OPTIMIZATION: Phase 6 - Server-side pagination implementation
 *
 * Changes made:
 * - Replaced getProviders() with usePaginatedData(getProvidersPaginated) hook
 * - Added debounced search (300ms) - server-side text search across name, label, vat, phone, email, address
 * - Analytics data fetched separately via fetchProviderAnalytics() to avoid computing from paginated data
 * - Server-side pagination with limit/offset
 * - Server-side sorting by name ASC (default)
 *
 * Benefits:
 * - Only loads visible page data (e.g., 25 records instead of 1000+)
 * - Search queries happen on server, reducing client memory usage
 * - Faster initial load and navigation between pages
 */
import React, { useEffect, useState, useMemo, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Grid,
  Avatar,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import SearchIcon from "@mui/icons-material/Search"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"
import StorefrontIcon from "@mui/icons-material/Storefront"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import HomeIcon from "@mui/icons-material/Home"
import BarChartIcon from "@mui/icons-material/BarChart"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import BusinessIcon from "@mui/icons-material/Business"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { getProvidersPaginated, fetchProviderAnalytics } from "../../helpers/api_helper"
import { usePaginatedData } from "../../hooks/usePaginatedData"

const MuiProvidersList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  document.title = t("Suppliers") + " | FlexFlow ERP"

  // Analytics state (fetched separately from paginated data)
  const [analytics, setAnalytics] = useState({
    total: 0,
    withPhone: 0,
    withEmail: 0,
    withVat: 0,
    withAddress: 0,
    cityChartData: []
  })
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)

  // ============================================================================
  // SERVER-SIDE PAGINATION via usePaginatedData hook
  // ============================================================================
  const {
    data: providers,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    refresh
  } = usePaginatedData(getProvidersPaginated, {
    initialPageSize: 25,
    debounceMs: 300,
    autoFetch: true
  })

  // ============================================================================
  // FETCH ANALYTICS SEPARATELY (lightweight query)
  // ============================================================================
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    try {
      const data = await fetchProviderAnalytics()
      setAnalytics(data)
    } catch (err) {
      console.error("Error loading analytics:", err)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  // Load analytics on mount and when location changes
  useEffect(() => {
    loadAnalytics()
  }, [location.key, loadAnalytics])

  // ============================================================================
  // CHART DATA (derived from analytics)
  // ============================================================================
  const contactChartData = useMemo(() => {
    return [
      { name: t("With Phone"), count: analytics.withPhone, color: "#10b981" },
      { name: t("With Email"), count: analytics.withEmail, color: "#6366f1" },
      { name: t("With VAT"), count: analytics.withVat, color: "#f59e0b" },
      { name: t("With Address"), count: analytics.withAddress, color: "#8b5cf6" },
    ]
  }, [analytics, t])

  const handleRowClick = (provider) => {
    navigate(`/provider-detail/${provider.id}`)
  }

  const handleRefresh = useCallback(() => {
    refresh()
    loadAnalytics()
  }, [refresh, loadAnalytics])

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
              <BusinessIcon sx={{ fontSize: 16, mr: 0.5, color: "#8b5cf6" }} />
              {t("Suppliers")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <IconButton size="small" onClick={handleRefresh} sx={{ color: "#6b7280", width: 32, height: 32 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Expandable Analytics Panel */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          sx={{
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f9fafb" : "#fff",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}>
              {t("Analytics")}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: 2 }}>
              {analytics.total} {t("suppliers")}
            </Typography>
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {analyticsExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            )}
          </IconButton>
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            {/* Stats Summary */}
            <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <StorefrontIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Total")}: <strong style={{ color: "#111827" }}>{analytics.total}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon sx={{ fontSize: 16, color: "#10b981" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Phone")}: <strong style={{ color: "#111827" }}>{analytics.withPhone}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Email")}: <strong style={{ color: "#111827" }}>{analytics.withEmail}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptLongIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With VAT")}: <strong style={{ color: "#111827" }}>{analytics.withVat}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
                <LocationOnIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Address")}: <strong style={{ color: "#111827" }}>{analytics.withAddress}</strong>
                </Typography>
              </Stack>
            </Stack>

            {/* Charts */}
            <Grid container spacing={2}>
              {/* Contact Info Completeness Chart */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Contact Info Completeness")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={contactChartData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#6b7280" }} width={75} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                        formatter={(value) => [value, t("Suppliers")]}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {contactChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Suppliers by Location Chart */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Top Locations")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  {analytics.cityChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.cityChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                          formatter={(value) => [value, t("Suppliers")]}
                        />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {analytics.cityChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af" }}>
                      <Typography sx={{ fontSize: "0.813rem" }}>{t("No location data available")}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Search */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t("Search by name, VAT, phone, email, address...")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{
              width: 320,
              "& .MuiOutlinedInput-root": {
                height: 32,
                fontSize: "0.813rem",
                bgcolor: "#f9fafb",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: "#8b5cf6" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}>
            {total} {t("results")}
          </Typography>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Supplier")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Contact")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Phone")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Email")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("VAT")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Address")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providers.length > 0 ? (
                    providers.map((provider) => (
                      <TableRow
                        key={provider.id}
                        hover
                        onClick={() => handleRowClick(provider)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } }}
                      >
                        <TableCell sx={{ py: 1.25 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: "#f3e8ff", color: "#8b5cf6", fontSize: "0.75rem" }}>
                              {(provider.name || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
                                {provider.name || "-"}
                              </Typography>
                              {provider.label && (
                                <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                                  {provider.label}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {provider.representor || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {provider.phone || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {provider.email || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", fontFamily: "monospace", color: "#374151" }}>
                            {provider.vat || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#6b7280", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {provider.address || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <BusinessIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                          {searchTerm ? t("No suppliers found matching your search") : t("No suppliers found")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={pagination.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("Rows")}
              sx={{
                borderTop: "1px solid #e5e7eb",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.75rem",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  )
}

export default MuiProvidersList
